import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Loader2,
  Send,
  Sparkles,
  Check,
  ExternalLink,
  Network,
  ChevronDown,
  RefreshCw,
  Clock,
  Library,
} from "lucide-react";
import { authClient, docsClient } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import { generateDocumentHash } from "@/lib/hash";
import CrawlProgressModal from "./CrawlProgressModal";
import CrawlResultsReview from "./CrawlResultsReview";

interface Message {
  role: "user" | "assistant";
  content: string;
  type?:
    | "suggestion"
    | "question"
    | "message"
    | "fix"
    | "update-tags"
    | "bulk-sync"
    | "search-existing";
  documents?: DocumentSuggestion[];
  tag_updates?: TagUpdateSuggestion[];
  sync_result?: SyncResult;
  existing_docs?: ExistingDocsResult;
  results?: {
    successful: number;
    failed: number;
    errors: string[];
  };
  jobs_created?: number;
}

interface DocumentSuggestion {
  url: string;
  name: string;
  description: string;
  tags: string[];
  confidence: number;
  reasoning: string;
}

interface TagUpdateSuggestion {
  id: string;
  title: string;
  current_tags: string[];
  suggested_tags: string[];
  reasoning: string;
  confidence: number;
  action: "replace" | "add";
}

interface StaleDocument {
  id: string;
  title: string;
  url: string;
  days_old: number;
  is_stale: boolean;
}

interface SyncResult {
  phase: "analyzing" | "complete";
  topic: string;
  existing_count: number;
  stale_count: number;
  stale_documents: StaleDocument[];
  message: string;
}

interface ExistingDocument {
  id: string;
  url: string;
  title: string;
  tags: string[];
  description: string;
  last_cleaned_at: string | null;
  created_at: string | null;
  size: number;
  estimated_tokens: number;
  days_old: number;
  is_stale: boolean;
  has_been_crawled: boolean;
  staleness_level: "fresh" | "aging" | "stale" | "critical" | "uncrawled";
}

interface ExistingDocsResult {
  total_count: number;
  documents: ExistingDocument[];
  stale_count: number;
  stale_documents: ExistingDocument[];
}

interface AIAddDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDocumentAdded: () => void;
}

export default function AIAddDocumentDialog({
  open,
  onOpenChange,
  onDocumentAdded,
}: AIAddDocumentDialogProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content:
        'Hi! I\'m here to help you find and add documentation to your team\'s library.\n\n💡 You can:\n• Request bulk downloads: "Download all MUI documentation"\n• Find specific docs: "FastAPI deployment guides"\n• Auto-discover: "Find comprehensive React docs"\n\nI\'ll automatically filter out documentation you already have. What are you looking for?',
      type: "message",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [addingDocs, setAddingDocs] = useState<Set<string>>(new Set());
  const [addingAll, setAddingAll] = useState(false);
  const [crawlJobId, setCrawlJobId] = useState<string | null>(null);
  const [crawlProgressOpen, setCrawlProgressOpen] = useState(false);
  const [crawlResultsOpen, setCrawlResultsOpen] = useState(false);

  // Auto-crawl settings
  const [autoCrawlEnabled, setAutoCrawlEnabled] = useState(true); // Default ON
  const [crawlSettings, setCrawlSettings] = useState({
    max_depth: 3,
    max_urls: 100,
  });
  const [settingsExpanded, setSettingsExpanded] = useState(false);

  const { toast } = useToast();

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setLoading(true);

    // Add user message to chat
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);

    try {
      // Call Edge Function - refresh JWT to ensure it's not expired
      const {
        data: { session },
        error: sessionError,
      } = await authClient.auth.refreshSession();

      if (sessionError || !session) {
        throw new Error(sessionError?.message || "Failed to refresh session");
      }

      const response = await fetch(
        `${import.meta.env.VITE_DOCS_SUPABASE_URL}/functions/v1/ai-document-discovery`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            message: userMessage,
            user_id: session?.user?.id,
            conversationHistory: messages
              .filter((m) => m.role === "user" || m.role === "assistant")
              .map((m) => ({
                role: m.role,
                content: m.content,
              })),
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get AI response");
      }

      const data = await response.json();
      const aiResponse = data.response;

      // Debug logging for response handling
      console.log("AI Response:", JSON.stringify(aiResponse, null, 2));
      console.log("Response type:", aiResponse?.type);
      console.log("Has documents:", !!aiResponse?.documents);
      console.log("Documents length:", aiResponse?.documents?.length);
      console.log("Is documents array:", Array.isArray(aiResponse?.documents));
      console.log("Documents typeof:", typeof aiResponse?.documents);

      // Handle error responses from backend (parsing failures, etc.)
      if (aiResponse?.type === "error") {
        console.error("AI Response Error:", aiResponse.error, aiResponse.debug_info);
        toast({
          title: "AI Processing Error",
          description: aiResponse.message || "Failed to process AI response. Please try again.",
          variant: "destructive",
        });
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              aiResponse.message ||
              "I encountered an error processing your request. Please try again.",
            type: "message",
          },
        ]);
        return;
      }

      // Handle edge case: backend returns type="message" but message contains embedded suggestion JSON
      // This happens when AI returns JSON followed by extra text, causing backend parse failure
      let effectiveResponse = aiResponse;
      if (aiResponse?.type === "message" && typeof aiResponse?.message === "string") {
        const messageStr = aiResponse.message;
        console.log("Message string (first 300 chars):", messageStr.substring(0, 300));
        console.log("Message contains opening brace:", messageStr.includes("{"));
        console.log("Message contains 'suggestion':", messageStr.includes("suggestion"));
        console.log("Message contains 'documents':", messageStr.includes("documents"));

        // Check if message looks like it contains embedded JSON (could be suggestion, fix, or update-tags)
        // The message might have the JSON with or without escaped quotes depending on backend processing
        const hasJsonMarkers =
          messageStr.includes("{") &&
          (messageStr.includes('"type"') ||
            messageStr.includes('\\"type\\"') ||
            messageStr.includes("type")) &&
          (messageStr.includes("suggestion") ||
            messageStr.includes("documents") ||
            messageStr.includes("fix"));

        if (hasJsonMarkers) {
          console.log("Detected embedded JSON in message, attempting extraction...");

          // Try direct parse first (in case the message IS valid JSON)
          try {
            const directParsed = JSON.parse(messageStr);
            if (directParsed && typeof directParsed === "object" && directParsed.type) {
              console.log("Direct JSON parse succeeded, type:", directParsed.type);
              effectiveResponse = directParsed;
            }
          } catch (directParseError) {
            // Direct parse failed, try extracting JSON from text
            console.log("Direct parse failed:", directParseError);
            console.log("Trying brace-matching extraction...");

            // First, try to unescape the entire string if it looks like escaped JSON
            // This handles: "{\"type\": \"suggestion\", ...}"
            if (messageStr.includes('\\"') && messageStr.trim().startsWith("{")) {
              try {
                const unescapedFull = messageStr.replace(/\\"/g, '"').replace(/\\\\/g, "\\");
                console.log("Trying full unescape parse...");
                const unescapedParsed = JSON.parse(unescapedFull);
                if (
                  unescapedParsed &&
                  typeof unescapedParsed === "object" &&
                  unescapedParsed.type
                ) {
                  console.log("Full unescape parse succeeded, type:", unescapedParsed.type);
                  effectiveResponse = unescapedParsed;
                }
              } catch (unescapeError) {
                console.log("Full unescape parse failed:", unescapeError);
              }
            }

            // If full unescape didn't work, try brace-matching extraction
            if (effectiveResponse === aiResponse) {
              try {
                // Extract JSON by finding balanced braces
                const startIdx = messageStr.indexOf("{");
                if (startIdx !== -1) {
                  let braceCount = 0;
                  let inString = false;
                  let escapeNext = false;
                  let endIdx = -1;

                  for (let i = startIdx; i < messageStr.length; i++) {
                    const char = messageStr[i];
                    if (escapeNext) {
                      escapeNext = false;
                      continue;
                    }
                    if (char === "\\") {
                      escapeNext = true;
                      continue;
                    }
                    if (char === '"') {
                      inString = !inString;
                      continue;
                    }
                    if (inString) continue;
                    if (char === "{") braceCount++;
                    else if (char === "}") {
                      braceCount--;
                      if (braceCount === 0) {
                        endIdx = i;
                        break;
                      }
                    }
                  }

                  if (endIdx !== -1) {
                    const extractedJson = messageStr.substring(startIdx, endIdx + 1);
                    console.log(
                      "Extracted JSON string (first 200 chars):",
                      extractedJson.substring(0, 200)
                    );

                    // Try parsing extracted JSON directly first
                    let parsed;
                    try {
                      parsed = JSON.parse(extractedJson);
                    } catch {
                      // If direct parse fails, the JSON might be double-escaped (e.g., \" instead of ")
                      // This happens when the backend wraps JSON in a message string
                      console.log("Direct parse of extracted JSON failed, trying unescape...");

                      // Check if the string looks double-escaped (contains \")
                      if (extractedJson.includes('\\"')) {
                        // Unescape: replace \" with " and \\ with \
                        const unescaped = extractedJson.replace(/\\"/g, '"').replace(/\\\\/g, "\\");
                        console.log(
                          "Unescaped JSON (first 200 chars):",
                          unescaped.substring(0, 200)
                        );
                        parsed = JSON.parse(unescaped);
                      } else {
                        // Not double-escaped, re-throw original error
                        throw new Error("JSON extraction failed - not double-escaped");
                      }
                    }

                    // Accept any valid response type with expected structure
                    if (
                      parsed.type &&
                      (parsed.documents || parsed.document_ids || parsed.message)
                    ) {
                      console.log("Successfully extracted embedded JSON, type:", parsed.type);
                      effectiveResponse = parsed;
                    }
                  }
                }
              } catch (e) {
                console.warn("Failed to extract embedded JSON from message:", e);
              }
            }
          }
        }
      }

      // Ensure documents is a proper array (handles edge cases)
      let documents = aiResponse?.documents;
      if (documents && !Array.isArray(documents)) {
        // Try to convert if it's array-like or stringified
        if (typeof documents === "string") {
          try {
            documents = JSON.parse(documents);
          } catch (e) {
            console.warn("Failed to parse documents string:", e);
            documents = [];
          }
        } else if (typeof documents === "object") {
          documents = Object.values(documents);
        }
      }

      // Add assistant message to chat
      if (aiResponse?.type === "suggestion" && Array.isArray(documents) && documents.length > 0) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `I found ${documents.length} documentation source(s) for you. Review them below and click "Add to Library" for the ones you want.`,
            type: "suggestion",
            documents: documents as DocumentSuggestion[],
          },
        ]);
      } else if (aiResponse?.type === "fix") {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: aiResponse.message,
            type: "fix",
            jobs_created: aiResponse.jobs_created,
          },
        ]);
        // Show success toast
        toast({
          title: "Fix Queued",
          description: `${aiResponse.jobs_created} job(s) created to re-download documents. Check the Jobs page for progress.`,
        });
      } else if (aiResponse?.type === "update-tags") {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              aiResponse.message ||
              `I found ${aiResponse.documents.length} document(s) that should have updated tags. Review and approve below.`,
            type: "update-tags",
            tag_updates: aiResponse.documents,
          },
        ]);
      } else if (aiResponse?.type === "bulk-sync") {
        // Handle bulk-sync response with stale documents
        const syncResult = aiResponse.sync_result as SyncResult;
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: syncResult?.message || "Analyzing your documentation library...",
            type: "bulk-sync",
            sync_result: syncResult,
          },
        ]);
      } else if (aiResponse?.type === "question") {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: aiResponse.message,
            type: "question",
          },
        ]);
      } else if (aiResponse?.type === "search-existing") {
        // Handle search-existing response - display existing docs from user's library
        const existingDocs = aiResponse.existing_docs as ExistingDocsResult;
        const totalCount = existingDocs?.total_count || 0;
        const staleCount = existingDocs?.stale_count || 0;

        let messageContent: string;
        if (totalCount === 0) {
          messageContent =
            aiResponse.message || "I couldn't find any matching documents in your library.";
        } else if (staleCount > 0) {
          messageContent =
            aiResponse.message ||
            `Found ${totalCount} document(s) in your library. ${staleCount} may need refreshing (over 7 days old).`;
        } else {
          messageContent = aiResponse.message || `Found ${totalCount} document(s) in your library.`;
        }

        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: messageContent,
            type: "search-existing",
            existing_docs: existingDocs,
          },
        ]);
      } else {
        // Fallback: ensure content is always a string
        let messageContent: string;
        if (typeof aiResponse === "string") {
          messageContent = aiResponse;
        } else if (aiResponse?.message && typeof aiResponse.message === "string") {
          messageContent = aiResponse.message;
        } else {
          // Last resort: stringify the response
          messageContent =
            "I received an unexpected response format. Here's what I got:\n\n" +
            JSON.stringify(aiResponse, null, 2);
          console.warn("Unexpected AI response format:", aiResponse);
        }

        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: messageContent,
            type: "message",
          },
        ]);
      }
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: "Error",
        description: "Failed to get AI response. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addDocument = async (doc: DocumentSuggestion, skipToast = false) => {
    setAddingDocs((prev) => new Set(prev).add(doc.url));

    try {
      // Get user from Agency-Auth SSO
      const {
        data: { user },
      } = await authClient.auth.getUser();

      if (!user) {
        throw new Error("Not authenticated");
      }

      // Check if URL already exists - use docsClient for data operations
      const { data: existing } = await docsClient
        .from("documents")
        .select("id, title, url")
        .eq("user_id", user.id)
        .eq("url", doc.url)
        .single();

      if (existing) {
        // Document exists - but if auto-crawl is enabled, still crawl for NEW related pages
        if (autoCrawlEnabled) {
          try {
            const crawlResult = await initiateCrawl(doc.url, {
              max_depth: crawlSettings.max_depth,
              max_urls: crawlSettings.max_urls,
            });

            if (crawlResult?.job_id) {
              setCrawlJobId(crawlResult.job_id);
              setCrawlProgressOpen(true);
              if (!skipToast) {
                toast({
                  title: "Syncing Documentation",
                  description: `"${existing.title}" exists. Crawling for new or updated pages...`,
                });
              }
            }
          } catch (crawlError) {
            console.error("Crawl initiation failed for existing doc:", crawlError);
            const errorMessage = crawlError instanceof Error ? crawlError.message : "Unknown error";
            if (!skipToast) {
              toast({
                title: "Sync Failed",
                description: `Could not crawl for updates: ${errorMessage}`,
                variant: "destructive",
              });
            }
          }
        } else {
          // Auto-crawl disabled - just show "already exists"
          if (!skipToast) {
            toast({
              title: "Already exists",
              description: `"${existing.title}" is already in your library`,
              variant: "default",
            });
          }
        }
        setAddingDocs((prev) => {
          const next = new Set(prev);
          next.delete(doc.url);
          return next;
        });
        return { success: false, reason: "duplicate" };
      }

      // Generate SHA-256 hash from URL
      const hash = await generateDocumentHash(doc.url);

      const { data: insertedDoc, error } = await docsClient
        .from("documents")
        .insert({
          url: doc.url,
          title: doc.name,
          description: doc.description,
          tags: doc.tags,
          user_id: user.id,
          hash: hash,
          processing_status: "pending",
        })
        .select()
        .single();

      if (error) throw error;

      if (!skipToast) {
        toast({
          title: "Success",
          description: `Added "${doc.name}" to your library. Content is being downloaded...`,
        });
      }

      // Trigger background processing
      if (insertedDoc) {
        // Get JWT from Agency-Auth SSO for edge function authorization
        const {
          data: { session },
        } = await authClient.auth.getSession();
        fetch(`${import.meta.env.VITE_DOCS_SUPABASE_URL}/functions/v1/process-document`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            document_id: insertedDoc.id,
          }),
        }).catch((err) => {
          console.error("Background processing failed:", err);
        });
      }

      if (!skipToast) {
        onDocumentAdded();
      }

      // NEW: If auto-crawl enabled, trigger crawl after document creation
      if (autoCrawlEnabled && insertedDoc) {
        try {
          const crawlResult = await initiateCrawl(doc.url, {
            max_depth: crawlSettings.max_depth,
            max_urls: crawlSettings.max_urls,
          });

          if (crawlResult?.job_id) {
            setCrawlJobId(crawlResult.job_id);
            setCrawlProgressOpen(true);
          }
        } catch (crawlError) {
          // Document was added successfully, but crawl failed to start
          console.error("Crawl initiation failed:", crawlError);
          const errorMessage = crawlError instanceof Error ? crawlError.message : "Unknown error";
          if (!skipToast) {
            toast({
              title: "Document Added, Crawl Failed",
              description: `"${doc.name}" was added. Crawl error: ${errorMessage}. You can manually trigger crawl from the Documents page.`,
              variant: "default",
            });
          }
        }
      }

      return { success: true };
    } catch (error) {
      console.error("Error adding document:", error);
      if (!skipToast) {
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "Failed to add document",
          variant: "destructive",
        });
      }
      return { success: false, reason: "error", error };
    } finally {
      setAddingDocs((prev) => {
        const next = new Set(prev);
        next.delete(doc.url);
        return next;
      });
    }
  };

  const addAllDocuments = async (docs: DocumentSuggestion[]) => {
    setAddingAll(true);
    let added = 0;
    let skipped = 0;
    let failed = 0;

    for (const doc of docs) {
      const result = await addDocument(doc, true);
      if (result?.success) {
        added++;
      } else if (result?.reason === "duplicate") {
        skipped++;
      } else {
        failed++;
      }
    }

    toast({
      title: "Bulk Add Complete",
      description: `Added ${added} documents. Skipped ${skipped} duplicates. ${failed > 0 ? `Failed: ${failed}` : ""}`,
    });

    setAddingAll(false);
    onDocumentAdded();
  };

  const applyTagUpdate = async (update: TagUpdateSuggestion, skipToast = false) => {
    setAddingDocs((prev) => new Set(prev).add(update.id));

    try {
      const {
        data: { user },
      } = await authClient.auth.getUser();

      if (!user) throw new Error("Not authenticated");

      // Get current document tags
      const { data: doc, error: docFetchError } = await docsClient
        .from("documents")
        .select("tags, user_id")
        .eq("id", update.id)
        .single();

      if (docFetchError || !doc) {
        console.error("Document fetch error:", docFetchError);
        console.error("Looking for document ID:", update.id);
        console.error("Current user ID:", user.id);
        throw new Error(`Document not found: ${docFetchError?.message || "Unknown error"}`);
      }

      // Verify user owns this document
      if (doc.user_id !== user.id) {
        throw new Error("You don't have permission to update this document");
      }

      // Determine final tags based on action
      let finalTags = update.suggested_tags;
      if (update.action === "add") {
        finalTags = [...new Set([...(doc.tags || []), ...update.suggested_tags])];
      }

      // Update tags using batch function
      // @ts-expect-error - Supabase generated types issue
      const { error } = await docsClient.rpc("batch_update_tags", {
        p_document_ids: [update.id],
        p_tags: finalTags,
      });

      if (error) throw error;

      if (!skipToast) {
        toast({
          title: "Success",
          description: `Updated tags for "${update.title}"`,
        });
        onDocumentAdded();
      }

      return { success: true };
    } catch (error) {
      console.error("Error updating tags:", error);
      if (!skipToast) {
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "Failed to update tags",
          variant: "destructive",
        });
      }
      return { success: false, reason: "error", error };
    } finally {
      setAddingDocs((prev) => {
        const next = new Set(prev);
        next.delete(update.id);
        return next;
      });
    }
  };

  const applyAllTagUpdates = async (updates: TagUpdateSuggestion[]) => {
    setAddingAll(true);
    let updated = 0;
    let failed = 0;

    for (const update of updates) {
      const result = await applyTagUpdate(update, true);
      if (result?.success) {
        updated++;
      } else {
        failed++;
      }
    }

    toast({
      title: "Bulk Tag Update Complete",
      description: `Updated ${updated} documents. ${failed > 0 ? `Failed: ${failed}` : ""}`,
    });

    setAddingAll(false);
    onDocumentAdded();
  };

  // Refresh a single stale document by triggering crawl
  const refreshStaleDocument = async (doc: StaleDocument, skipToast = false) => {
    setAddingDocs((prev) => new Set(prev).add(doc.id));

    try {
      const crawlResult = await initiateCrawl(doc.url, {
        max_depth: crawlSettings.max_depth,
        max_urls: crawlSettings.max_urls,
      });

      if (crawlResult?.job_id) {
        setCrawlJobId(crawlResult.job_id);
        setCrawlProgressOpen(true);
        if (!skipToast) {
          toast({
            title: "Refreshing Documentation",
            description: `Crawling "${doc.title}" for updates...`,
          });
        }
      }
      return { success: true };
    } catch (error) {
      console.error("Error refreshing document:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      if (!skipToast) {
        toast({
          title: "Refresh Failed",
          description: `Could not refresh "${doc.title}": ${errorMessage}`,
          variant: "destructive",
        });
      }
      return { success: false, reason: "error", error };
    } finally {
      setAddingDocs((prev) => {
        const next = new Set(prev);
        next.delete(doc.id);
        return next;
      });
    }
  };

  // Refresh all stale documents
  const refreshAllStaleDocuments = async (docs: StaleDocument[]) => {
    setAddingAll(true);
    let refreshed = 0;
    let failed = 0;

    for (const doc of docs) {
      const result = await refreshStaleDocument(doc, true);
      if (result?.success) {
        refreshed++;
      } else {
        failed++;
      }
    }

    toast({
      title: "Bulk Refresh Complete",
      description: `Started refresh for ${refreshed} documents. ${failed > 0 ? `Failed: ${failed}` : ""}`,
    });

    setAddingAll(false);
  };

  // Initiate crawl from entry URL
  const initiateCrawl = async (
    entryUrl: string,
    settings?: { max_depth?: number; max_urls?: number }
  ) => {
    try {
      // Get JWT from Agency-Auth SSO
      const {
        data: { session },
        error: sessionError,
      } = await authClient.auth.refreshSession();

      if (sessionError || !session) {
        throw new Error(sessionError?.message || "Failed to refresh session");
      }

      // Call crawl-documentation edge function
      const response = await fetch(
        `${import.meta.env.VITE_DOCS_SUPABASE_URL}/functions/v1/crawl-documentation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            entry_url: entryUrl,
            max_depth: settings?.max_depth || crawlSettings.max_depth,
            max_urls: settings?.max_urls || crawlSettings.max_urls,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        // Include details from edge function error response
        const errorMessage = errorData.details
          ? `${errorData.error}: ${errorData.details}`
          : errorData.error || `HTTP ${response.status}`;
        throw new Error(errorMessage);
      }

      const data = await response.json();
      const jobId = data.job_id;

      if (!jobId) {
        throw new Error("No job ID returned from crawl");
      }

      // Open progress modal if not called from auto-crawl
      if (!settings) {
        setCrawlJobId(jobId);
        setCrawlProgressOpen(true);

        toast({
          title: "Crawl Started",
          description: "Discovering related documentation pages...",
        });
      }

      return { job_id: jobId, status: data.status };
    } catch (error) {
      console.error("Error starting crawl:", error);
      // Only show toast if not called from auto-crawl (auto-crawl handles its own error toast)
      if (!settings) {
        toast({
          title: "Crawl Failed",
          description: error instanceof Error ? error.message : "Failed to start crawl",
          variant: "destructive",
        });
      }
      throw error;
    }
  };

  // Handle crawl completion
  const handleCrawlComplete = (jobId: string) => {
    setCrawlProgressOpen(false);
    setCrawlResultsOpen(true);
  };

  // Handle crawl cancel
  const handleCrawlCancel = (jobId: string) => {
    setCrawlJobId(null);
    toast({
      title: "Crawl Cancelled",
      description: "The documentation crawl was cancelled.",
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[80vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Document Discovery
          </DialogTitle>
          <DialogDescription>
            Describe what documentation you're looking for, and I'll help you find it
          </DialogDescription>
        </DialogHeader>

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto px-6 min-h-0">
          <div className="space-y-4 py-4">
            {/* Auto-Crawl Settings - Always visible at top */}
            <Collapsible open={settingsExpanded} onOpenChange={setSettingsExpanded}>
              <Card className="border-primary/20 bg-primary/5">
                <CardHeader className="pb-3">
                  <CollapsibleTrigger className="flex items-center justify-between w-full [&[data-state=open]>svg]:rotate-180">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={autoCrawlEnabled}
                        onCheckedChange={setAutoCrawlEnabled}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="text-left">
                        <CardTitle className="text-sm">Auto-Crawl Related Pages</CardTitle>
                        <CardDescription className="text-xs mt-1">
                          Automatically discover documentation when adding URLs
                        </CardDescription>
                      </div>
                    </div>
                    <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
                  </CollapsibleTrigger>
                </CardHeader>
                <CollapsibleContent>
                  <CardContent className="pb-3 pt-0">
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-xs font-medium">Max Depth</Label>
                          <Badge variant="outline" className="text-xs">
                            {crawlSettings.max_depth}
                          </Badge>
                        </div>
                        <Slider
                          min={1}
                          max={5}
                          step={1}
                          value={[crawlSettings.max_depth]}
                          onValueChange={([v]) =>
                            setCrawlSettings({ ...crawlSettings, max_depth: v })
                          }
                          className="w-full"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          How many clicks from entry URL (3 = homepage → guides → API reference)
                        </p>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-xs font-medium">Max Pages</Label>
                          <Badge variant="outline" className="text-xs">
                            {crawlSettings.max_urls}
                          </Badge>
                        </div>
                        <Slider
                          min={10}
                          max={500}
                          step={10}
                          value={[crawlSettings.max_urls]}
                          onValueChange={([v]) =>
                            setCrawlSettings({ ...crawlSettings, max_urls: v })
                          }
                          className="w-full"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Maximum documentation pages to discover per domain
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {message.role === "assistant" ? (
                  <div className="max-w-[85%] space-y-2">
                    <div className="bg-muted p-3 rounded-lg">
                      <p className="text-sm whitespace-pre-wrap">
                        {typeof message.content === "string"
                          ? message.content
                          : typeof message.content === "object" && message.content !== null
                            ? (message.content as { message?: string }).message ||
                              JSON.stringify(message.content, null, 2)
                            : String(message.content)}
                      </p>
                    </div>

                    {/* Document Suggestions */}
                    {message.documents && message.documents.length > 0 && (
                      <div className="space-y-2">
                        {/* Add All Button */}
                        {message.documents.length > 1 && (
                          <Button
                            onClick={() => addAllDocuments(message.documents!)}
                            disabled={addingAll || addingDocs.size > 0}
                            variant="default"
                            className="w-full"
                          >
                            {addingAll ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Adding {message.documents.length} documents...
                              </>
                            ) : autoCrawlEnabled ? (
                              <>
                                <Network className="mr-2 h-4 w-4" />
                                Add & Crawl All {message.documents.length} Documents
                              </>
                            ) : (
                              <>
                                <Check className="mr-2 h-4 w-4" />
                                Add All {message.documents.length} Documents
                              </>
                            )}
                          </Button>
                        )}

                        {message.documents.map((doc, docIndex) => (
                          <Card key={docIndex} className="border-primary/20">
                            <CardHeader className="pb-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <CardTitle className="text-base flex items-center gap-2">
                                    {doc.name}
                                    <Badge variant="outline" className="text-xs">
                                      {Math.round(doc.confidence * 100)}% match
                                    </Badge>
                                  </CardTitle>
                                  <CardDescription className="mt-1 text-xs line-clamp-2">
                                    {doc.description}
                                  </CardDescription>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent className="pb-3">
                              <div className="flex flex-wrap gap-1 mb-2">
                                {doc.tags.map((tag) => (
                                  <Badge key={tag} variant="secondary" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <ExternalLink className="h-3 w-3" />
                                <a
                                  href={doc.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:underline truncate"
                                >
                                  {doc.url}
                                </a>
                              </div>
                              {doc.reasoning && (
                                <p className="text-xs text-muted-foreground mt-2 italic">
                                  💡 {doc.reasoning}
                                </p>
                              )}
                            </CardContent>
                            <CardFooter className="pt-0 flex gap-2">
                              <Button
                                onClick={() => addDocument(doc)}
                                disabled={addingDocs.has(doc.url)}
                                size="sm"
                                className="flex-1"
                              >
                                {addingDocs.has(doc.url) ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Adding...
                                  </>
                                ) : autoCrawlEnabled ? (
                                  <>
                                    <Network className="mr-2 h-4 w-4" />
                                    Add & Crawl
                                  </>
                                ) : (
                                  <>
                                    <Check className="mr-2 h-4 w-4" />
                                    Add to Library
                                  </>
                                )}
                              </Button>
                              {!autoCrawlEnabled && (
                                <Button
                                  onClick={() => initiateCrawl(doc.url)}
                                  disabled={addingDocs.has(doc.url)}
                                  size="sm"
                                  variant="outline"
                                  className="flex-1"
                                >
                                  <Network className="mr-2 h-4 w-4" />
                                  Crawl Related Pages
                                </Button>
                              )}
                            </CardFooter>
                          </Card>
                        ))}
                      </div>
                    )}

                    {/* Tag Update Suggestions */}
                    {message.tag_updates && message.tag_updates.length > 0 && (
                      <div className="space-y-2">
                        {/* Apply All Button */}
                        {message.tag_updates.length > 1 && (
                          <Button
                            onClick={() => applyAllTagUpdates(message.tag_updates!)}
                            disabled={addingAll || addingDocs.size > 0}
                            variant="default"
                            className="w-full"
                          >
                            {addingAll ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Updating {message.tag_updates.length} documents...
                              </>
                            ) : (
                              <>
                                <Check className="mr-2 h-4 w-4" />
                                Apply All Tag Updates
                              </>
                            )}
                          </Button>
                        )}

                        {message.tag_updates.map((update, updateIndex) => (
                          <Card key={updateIndex} className="border-amber-500/20 bg-amber-500/5">
                            <CardHeader className="pb-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                                    {update.title}
                                    <Badge variant="outline" className="text-xs">
                                      {Math.round(update.confidence * 100)}% confidence
                                    </Badge>
                                    <Badge
                                      variant={
                                        update.action === "replace" ? "destructive" : "default"
                                      }
                                      className="text-xs"
                                    >
                                      {update.action === "replace" ? "Replace All" : "Add Tags"}
                                    </Badge>
                                  </CardTitle>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent className="pb-3">
                              {/* Current Tags */}
                              <div className="mb-2">
                                <p className="text-xs font-semibold text-muted-foreground mb-1">
                                  Current Tags:
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {update.current_tags.length > 0 ? (
                                    update.current_tags.map((tag) => (
                                      <Badge key={tag} variant="secondary" className="text-xs">
                                        {tag}
                                      </Badge>
                                    ))
                                  ) : (
                                    <span className="text-xs text-muted-foreground italic">
                                      No tags
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Suggested Tags */}
                              <div className="mb-2">
                                <p className="text-xs font-semibold text-muted-foreground mb-1">
                                  Suggested Tags:
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {update.suggested_tags.map((tag) => (
                                    <Badge key={tag} variant="default" className="text-xs">
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              </div>

                              {/* Reasoning */}
                              {update.reasoning && (
                                <p className="text-xs text-muted-foreground mt-2 italic">
                                  💡 {update.reasoning}
                                </p>
                              )}
                            </CardContent>
                            <CardFooter className="pt-0">
                              <Button
                                onClick={() => applyTagUpdate(update)}
                                disabled={addingDocs.has(update.id)}
                                size="sm"
                                className="w-full"
                              >
                                {addingDocs.has(update.id) ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Updating...
                                  </>
                                ) : (
                                  <>
                                    <Check className="mr-2 h-4 w-4" />
                                    Apply Tag Update
                                  </>
                                )}
                              </Button>
                            </CardFooter>
                          </Card>
                        ))}
                      </div>
                    )}

                    {/* Bulk Sync Results - Stale Documents */}
                    {message.sync_result && message.sync_result.stale_documents.length > 0 && (
                      <div className="space-y-2">
                        {/* Summary Card */}
                        <Card className="border-blue-500/20 bg-blue-500/5">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <Clock className="h-4 w-4 text-blue-500" />
                              {message.sync_result.topic} Documentation Status
                            </CardTitle>
                            <CardDescription className="text-xs">
                              {message.sync_result.existing_count} documents found,{" "}
                              {message.sync_result.stale_count} need refresh
                            </CardDescription>
                          </CardHeader>
                        </Card>

                        {/* Refresh All Button */}
                        {message.sync_result.stale_documents.length > 1 && (
                          <Button
                            onClick={() =>
                              refreshAllStaleDocuments(message.sync_result!.stale_documents)
                            }
                            disabled={addingAll || addingDocs.size > 0}
                            variant="default"
                            className="w-full"
                          >
                            {addingAll ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Refreshing {message.sync_result.stale_documents.length} documents...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Refresh All {message.sync_result.stale_documents.length} Stale Docs
                              </>
                            )}
                          </Button>
                        )}

                        {/* Individual Stale Documents */}
                        {message.sync_result.stale_documents.map((doc, docIndex) => (
                          <Card key={docIndex} className="border-amber-500/20">
                            <CardHeader className="pb-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <CardTitle className="text-base flex items-center gap-2">
                                    {doc.title}
                                    <Badge variant="outline" className="text-xs text-amber-600">
                                      <Clock className="h-3 w-3 mr-1" />
                                      {doc.days_old} days old
                                    </Badge>
                                  </CardTitle>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent className="pb-3">
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <ExternalLink className="h-3 w-3" />
                                <a
                                  href={doc.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:underline truncate"
                                >
                                  {doc.url}
                                </a>
                              </div>
                            </CardContent>
                            <CardFooter className="pt-0">
                              <Button
                                onClick={() => refreshStaleDocument(doc)}
                                disabled={addingDocs.has(doc.id)}
                                size="sm"
                                className="w-full"
                              >
                                {addingDocs.has(doc.id) ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Refreshing...
                                  </>
                                ) : (
                                  <>
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Refresh Documentation
                                  </>
                                )}
                              </Button>
                            </CardFooter>
                          </Card>
                        ))}
                      </div>
                    )}

                    {/* Search Existing Results - Display existing documents from user's library */}
                    {message.existing_docs && message.existing_docs.documents.length > 0 && (
                      <div className="space-y-2">
                        {/* Summary Card */}
                        <Card className="border-green-500/20 bg-green-500/5">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <Library className="h-4 w-4 text-green-500" />
                              Your Library Results
                            </CardTitle>
                            <CardDescription className="text-xs">
                              {message.existing_docs.total_count} document(s) found
                              {message.existing_docs.stale_count > 0 && (
                                <span className="text-amber-600">
                                  {" "}
                                  ({message.existing_docs.stale_count} may need refresh)
                                </span>
                              )}
                            </CardDescription>
                          </CardHeader>
                        </Card>

                        {/* Existing Documents List */}
                        {message.existing_docs.documents.map((doc, docIndex) => (
                          <Card
                            key={docIndex}
                            className={
                              !doc.has_been_crawled
                                ? "border-blue-500/20"
                                : doc.is_stale
                                  ? "border-amber-500/20"
                                  : "border-green-500/20"
                            }
                          >
                            <CardHeader className="pb-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <CardTitle className="text-base flex items-center gap-2">
                                    {doc.title}
                                    <Badge
                                      variant="outline"
                                      className={`text-xs ${
                                        doc.staleness_level === "uncrawled"
                                          ? "text-blue-600"
                                          : doc.staleness_level === "fresh"
                                            ? "text-green-600"
                                            : doc.staleness_level === "aging"
                                              ? "text-yellow-600"
                                              : doc.staleness_level === "stale"
                                                ? "text-amber-600"
                                                : "text-red-600"
                                      }`}
                                    >
                                      <Clock className="h-3 w-3 mr-1" />
                                      {doc.staleness_level === "uncrawled"
                                        ? "Not crawled"
                                        : doc.staleness_level === "fresh"
                                          ? "Fresh"
                                          : `${doc.days_old} days old`}
                                    </Badge>
                                  </CardTitle>
                                  {doc.description && (
                                    <CardDescription className="mt-1 text-xs line-clamp-2">
                                      {doc.description}
                                    </CardDescription>
                                  )}
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent className="pb-3">
                              {doc.tags && doc.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                  {doc.tags.map((tag) => (
                                    <Badge key={tag} variant="secondary" className="text-xs">
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <ExternalLink className="h-3 w-3" />
                                <a
                                  href={doc.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:underline truncate"
                                >
                                  {doc.url}
                                </a>
                              </div>
                              {doc.estimated_tokens && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  ~{doc.estimated_tokens.toLocaleString()} tokens
                                </p>
                              )}
                            </CardContent>
                            {(doc.is_stale || !doc.has_been_crawled) && (
                              <CardFooter className="pt-0">
                                <Button
                                  onClick={() =>
                                    refreshStaleDocument({
                                      id: doc.id,
                                      title: doc.title,
                                      url: doc.url,
                                      days_old: doc.days_old,
                                      is_stale: doc.is_stale,
                                    })
                                  }
                                  disabled={addingDocs.has(doc.id)}
                                  size="sm"
                                  className="w-full"
                                >
                                  {addingDocs.has(doc.id) ? (
                                    <>
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                      {doc.has_been_crawled ? "Refreshing..." : "Crawling..."}
                                    </>
                                  ) : (
                                    <>
                                      <RefreshCw className="mr-2 h-4 w-4" />
                                      {doc.has_been_crawled
                                        ? "Refresh Documentation"
                                        : "Crawl Documentation"}
                                    </>
                                  )}
                                </Button>
                              </CardFooter>
                            )}
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-primary text-primary-foreground p-3 rounded-lg max-w-[85%]">
                    <p className="text-sm">{message.content}</p>
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-muted p-3 rounded-lg">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Input */}
        <div className="border-t p-4 shrink-0">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Describe what documentation you need..."
              disabled={loading}
              className="flex-1"
            />
            <Button onClick={sendMessage} disabled={loading || !input.trim()}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Try: "Download all MUI documentation" or "Find comprehensive React docs"
          </p>
        </div>
      </DialogContent>

      {/* Crawl Progress Modal */}
      {crawlJobId && (
        <CrawlProgressModal
          open={crawlProgressOpen}
          onOpenChange={setCrawlProgressOpen}
          jobId={crawlJobId}
          onComplete={handleCrawlComplete}
          onCancel={handleCrawlCancel}
        />
      )}

      {/* Crawl Results Review Modal */}
      {crawlJobId && (
        <CrawlResultsReview
          open={crawlResultsOpen}
          onOpenChange={setCrawlResultsOpen}
          jobId={crawlJobId}
          onDocumentsAdded={onDocumentAdded}
        />
      )}
    </Dialog>
  );
}
